// src/core/score/mfhDealScore.ts
// Bruecke zwischen den MFH-Mechanik-Kennzahlen (core/metrics/mfh.ts) und dem
// analyzer-uebergreifenden Deal-Score (dealScore.ts). Gleiches Muster wie
// etwDealScore.ts (fromMetrics fuer die lebende Analyzer-Seite, fromSnapshotInput
// fuer "Meine Deals"/PDF-Export). Nutzt die vollen, bestehenden MFH-Metriken
// (nicht die /check-Pauschale) als NOI-/Annuitaet-/Stress-Quelle.

import { computeMfhMetrics, type MfhMetrics, type MfhMetricsInput } from "../metrics/mfh.js";
import { computeDealScore, type DealScoreResult, type LageInput } from "./dealScore.js";
import { MFH_SCORE_CONFIG, RENDITE_ZUSCHLAG_PP, BEWIRTSCHAFTUNG_PAUSCHALE_PCT } from "./dealScoreConfig.js";
import { resolveImportPcts, pctSum } from "../../lib/purchaseCosts.js";
import { resolveBewirtschaftungModus } from "../bewirtschaftung.js";
import type { MfhSnapshotInput } from "../../lib/mfhSnapshot.js";

const STRESS_ZINS_DELTA = 0.01;
const STRESS_MIETE_FAKTOR = 0.9;

function toDealScoreInput(
  metrics: MfhMetrics,
  stressMetrics: MfhMetrics,
  financingOn: boolean,
  lage: LageInput,
  vermietungsstatus: "vollstaendig" | "teilweise" | "nein"
) {
  return {
    kaufpreis: metrics.kaufpreisView,
    gesamtinvestition: metrics.allIn,
    jahreskaltmiete: metrics.grossRentAdj,
    noiJahr: metrics.noi,
    annuitaetJahr: metrics.annuitaetJahr,
    financingOn,
    stressNoiJahr: stressMetrics.noi,
    stressAnnuitaetJahr: stressMetrics.annuitaetJahr,
    lage,
    renditeZuschlagPp: RENDITE_ZUSCHLAG_PP.mfh,
    vermietungsstatus,
  };
}

/** Fuer die lebende Analyzer-Seite: metrics ist das Ergebnis des bereits erfolgten computeMfhMetrics()-Aufrufs. */
export function computeMfhDealScoreFromMetrics(
  baseInput: MfhMetricsInput,
  metrics: MfhMetrics,
  lage: LageInput,
  vermietungsstatus: "vollstaendig" | "teilweise" | "nein" = "vollstaendig"
): DealScoreResult {
  const stressInput: MfhMetricsInput = {
    ...baseInput,
    zins: baseInput.zins + STRESS_ZINS_DELTA,
    grossRentYear: baseInput.grossRentYear * STRESS_MIETE_FAKTOR,
    // Stress-Pass-Fix (18.10.2026): Pauschal bleibt unter Mietstress fix, berechnet auf
    // der Basis-Kaltmiete des unveraenderten Basis-Durchlaufs (siehe metrics/mfh.ts).
    pauschaleBasisMiete: metrics.effRentYear,
  };
  const stressMetrics = computeMfhMetrics(stressInput);
  // MFH hat keinen expliziten financingOn-Schalter (siehe metrics/mfh.ts) -- "finanziert"
  // bedeutet hier "es gibt ein Darlehen > 0", analog zur bestehenden Funnel-Konvention
  // in MFHCheck.tsx (`funnel.onCompleted({ financingOn: loan > 0, ... })`).
  const financingOn = metrics.loan > 0;
  return computeDealScore(
    toDealScoreInput(metrics, stressMetrics, financingOn, lage, vermietungsstatus),
    MFH_SCORE_CONFIG
  );
}

/** Gesamtflaeche aus dem rohen Snapshot-Input (gesamt-Modus: gesamtFlaecheM2, einheiten-
 *  Modus: Summe der Einheiten) -- fuer die Instandhaltungs-€/m²-Umrechnung unten. */
export function mfhFlaecheFromRawInput(input: { mgmtMode: "gesamt" | "einheiten"; gesamtFlaecheM2: number; units: { areaM2: number }[] }): number {
  return input.mgmtMode === "einheiten" ? input.units.reduce((s, u) => s + u.areaM2, 0) : input.gesamtFlaecheM2;
}

/**
 * Migrations-Bruecke Bewirtschaftung Pauschal/Einzeln (Baustein 5, Korrektur der frueheren
 * "3 % uebernehmen"-Vorgabe, NACHKORRIGIERT 17.10.2026 -- Pre-Push-Befund: die urspruengliche
 * Fassung migrierte zu "Pauschal", sobald nur capexRuecklagePctBrutto 0 war, und verwarf dabei
 * stillschweigend eine ggf. abweichende, real gespeicherte nicht-umlagefaehige-Kosten-Zahl).
 * Siehe core/bewirtschaftung.ts' resolveBewirtschaftungModus() fuer die jetzige Regel: "Pauschal"
 * nur, wenn die gespeicherten nicht-umlagefaehigen Kosten der Pauschale (auf effektive Miete)
 * exakt entsprechen (Toleranz 1 €) UND Instandhaltung 0 ist -- sonst "Einzeln" mit den
 * gespeicherten Werten 1:1 (Score bleibt in jedem Fall unveraendert).
 */
export function resolveMfhBewirtschaftung(
  rawInput: { bewirtschaftungModus?: unknown; capexRuecklagePctBrutto: number; nichtUmlagefaehigeKosten: number; verwaltung?: number; instandhaltungJeM2Jahr?: number },
  grossRentYear: number,
  leerstandPct: number,
  flaeche: number
): { bewirtschaftungModus: "pauschal" | "einzeln"; verwaltung: number; instandhaltungJeM2Jahr: number; migriertVonAlt: boolean; instandhaltungVermutlichEnthalten: boolean } {
  // Naeherung an effRentYear (die tatsaechliche Bemessungsgrundlage der Pauschale in
  // computeMfhMetrics) -- an dieser Stelle ist nur grossRentYear/leerstandPct bekannt, nicht
  // das fertige Metrics-Ergebnis. Playground-Preis-/Mietanpassungen bleiben hier unberuecksichtigt
  // (bei einem frisch geladenen Bestandsobjekt neutral) -- im ungluecklichsten Fall faellt die
  // Erkennung dadurch zu vorsichtig aus (Einzeln statt Pauschal), nie umgekehrt score-riskant.
  const effRentYearApprox = grossRentYear * (1 - Math.max(0, Math.min(1, leerstandPct)));
  const pauschaleBetrag = effRentYearApprox * BEWIRTSCHAFTUNG_PAUSCHALE_PCT.mfh;
  // erlaubePauschal=false (Live-Befund 17.10.2026, nach dem Push): MFHs Legacy-Modell behandelt
  // nicht-umlagefaehige Kosten als FIXEN €-Betrag, skaliert unter Stress NICHT mit der
  // gestressten Miete -- "Pauschal" (effRentYear x Pauschale%) skaliert IMMER mit, selbst wenn
  // der Basisbetrag exakt passt. Migration geht deshalb nie zu "Pauschal", siehe
  // core/bewirtschaftung.ts' Dateikommentar.
  const { modus, migriertVonAlt } = resolveBewirtschaftungModus(
    rawInput.bewirtschaftungModus, rawInput.capexRuecklagePctBrutto, rawInput.nichtUmlagefaehigeKosten, pauschaleBetrag, false
  );
  if (modus === "pauschal") {
    return { bewirtschaftungModus: "pauschal", verwaltung: 0, instandhaltungJeM2Jahr: 0, migriertVonAlt: false, instandhaltungVermutlichEnthalten: false };
  }
  let verwaltung: number, instandhaltungJeM2Jahr: number;
  if (migriertVonAlt) {
    const instandhaltungJahrAlt = rawInput.capexRuecklagePctBrutto * grossRentYear;
    verwaltung = 0;
    instandhaltungJeM2Jahr = flaeche > 0 ? instandhaltungJahrAlt / flaeche : 0;
  } else {
    verwaltung = rawInput.verwaltung ?? 0;
    instandhaltungJeM2Jahr = rawInput.instandhaltungJeM2Jahr ?? 0;
  }
  // Gilt gleichermassen fuer migrierte UND explizit gespeicherte "Einzeln"-Snapshots (z. B.
  // aus /check mit Override, siehe quickCheckGenericAdapter.ts) -- siehe core/bewirtschaftung.ts'
  // Dateikommentar.
  const instandhaltungVermutlichEnthalten = instandhaltungJeM2Jahr === 0 && rawInput.nichtUmlagefaehigeKosten >= pauschaleBetrag;
  return { bewirtschaftungModus: "einzeln", verwaltung, instandhaltungJeM2Jahr, migriertVonAlt, instandhaltungVermutlichEnthalten };
}

/**
 * Fuer "Meine Deals"/PDF: nur die gespeicherten Rohdaten vorhanden, kein live computeMfhMetrics()-
 * Ergebnis. `rawInput` ist der rohe gespeicherte Datensatz inkl. der Kaufnebenkosten-Felder, die
 * NICHT Teil von MfhSnapshotInput sind (eigenes Subsystem, siehe purchaseCosts.ts), analog zu
 * computeEtwDealScoreFromSnapshotInput.
 */
export function computeMfhDealScoreFromSnapshotInput(
  rawInput: MfhSnapshotInput & Record<string, unknown>,
  playground: { priceAdjPct: number; rentAdjPct: number; applyAdjustments: boolean },
  lage: LageInput,
  vermietungsstatus: "vollstaendig" | "teilweise" | "nein" = "vollstaendig"
): DealScoreResult {
  const input = rawInput;
  const grossRentYear =
    input.mgmtMode === "einheiten"
      ? input.units.reduce((s, u) => s + u.areaM2 * u.rentPerM2 * 12, 0)
      : input.kaltmieteJahr;
  const nkPct = pctSum(resolveImportPcts(rawInput, "mfh"));
  const flaeche = mfhFlaecheFromRawInput(input);
  const bewirtschaftung = resolveMfhBewirtschaftung(input, grossRentYear, input.leerstandPct, flaeche);
  const base: MfhMetricsInput = {
    kaufpreis: input.kaufpreis,
    priceAdjPct: playground.priceAdjPct,
    applyAdjustments: playground.applyAdjustments,
    nkPct,
    nkRenovierung: input.nkRenovierung,
    nkSanierung: input.nkSanierung,
    grossRentYear,
    rentAdjPct: playground.rentAdjPct,
    leerstandPct: input.leerstandPct,
    nichtUmlagefaehigeKosten: input.nichtUmlagefaehigeKosten,
    capexRuecklagePctBrutto: input.capexRuecklagePctBrutto,
    bewirtschaftungModus: bewirtschaftung.bewirtschaftungModus,
    verwaltung: bewirtschaftung.verwaltung,
    instandhaltungJeM2Jahr: bewirtschaftung.instandhaltungJeM2Jahr,
    flaeche,
    eigenkapital: input.eigenkapital,
    manualLoan: input.manualLoan,
    darlehenManual: input.darlehenManual,
    zins: input.zins,
    tilgung: input.tilgung,
  };
  const metrics = computeMfhMetrics(base);
  return computeMfhDealScoreFromMetrics(base, metrics, lage, vermietungsstatus);
}
